/**
 * Tab "Nguồn dữ liệu" của Tự động hoá v2 — kết nối THẬT với nền tảng ngoài.
 *  - KiotViet: nhập tên gian hàng + Client ID/Secret (lưu mã hoá ở máy chủ, không
 *    bao giờ trả lại nguyên văn) → Kiểm tra kết nối → Đăng ký webhook tự động →
 *    Kéo dữ liệu ngay. Máy chủ còn tự kéo phần thay đổi mỗi 5 phút làm dự phòng.
 *  - Webhook chung: URL riêng + khoá ký HMAC cho hệ thống khác POST dữ liệu vào.
 *  - Bảng "Sự kiện đã nhận" để kiểm tra dữ liệu có về không và đã chạy luồng nào.
 */
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Copy, Eye, EyeOff, KeyRound, Loader2, Plug, Plus, RefreshCw, Trash2, Webhook, PauseCircle, PlayCircle, CheckCircle2, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/misc'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { EmptyState } from '@/components/shared/feedback'
import { cn } from '@/lib/utils'
import { api, apiError } from '@/lib/api-client'
import { storeV2, useStoreV2 } from '@/hooks/use-automation-v2'
import { SOURCE_EVENTS, SOURCE_LABELS, type Connector, type SourceType } from '@/lib/automation-v2/types'
import { KIOTVIET_QUOTA } from '@/lib/automation-v2/kiotviet'

export const TRANG_THAI_NGUON: Record<Connector['status'], { nhan: string; v: 'success' | 'warning' | 'destructive' | 'secondary' }> = {
  connected: { nhan: 'Đã kết nối', v: 'success' }, pending: { nhan: 'Chưa kiểm tra', v: 'warning' },
  error: { nhan: 'Lỗi', v: 'destructive' }, paused: { nhan: 'Tạm dừng', v: 'secondary' },
}
/** Loại nguồn máy chủ đã hỗ trợ chạy thật. */
const LOAI_HO_TRO: SourceType[] = ['kiotviet', 'webhook']
const fmt = (s?: string | null) => (s ? new Date(s).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '—')

export function TabNguon() {
  const { connectors, daTai } = useStoreV2()
  const [chon, setChon] = useState<string | null>(null)
  const [loaiMoi, setLoaiMoi] = useState<SourceType>('kiotviet')
  const [dangTao, setDangTao] = useState(false)
  useEffect(() => { if (!chon && connectors[0]) setChon(connectors[0].id) }, [connectors, chon])
  const c = connectors.find((x) => x.id === chon) ?? null

  const tao = async () => {
    setDangTao(true)
    try { const n = await storeV2.newConnector(loaiMoi, `${SOURCE_LABELS[loaiMoi]} mới`); setChon(n.id) } finally { setDangTao(false) }
  }
  return (
    <div className="grid gap-3 lg:grid-cols-[18rem_1fr]">
      <div className="space-y-2">
        {!daTai && <p className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Đang tải…</p>}
        {connectors.map((x) => (
          <button key={x.id} type="button" onClick={() => setChon(x.id)} className={cn('flex w-full items-center gap-2 rounded-lg border p-2.5 text-left text-sm hover:bg-accent/50', chon === x.id && 'border-primary bg-primary/5')}>
            <Plug className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{x.name}</span>
              <span className="block text-[11px] text-muted-foreground">{SOURCE_LABELS[x.type]} · sự kiện cuối {fmt(x.lastEventAt)}</span>
            </span>
            <Badge variant={TRANG_THAI_NGUON[x.status].v} className="shrink-0 text-[10px]">{TRANG_THAI_NGUON[x.status].nhan}</Badge>
          </button>
        ))}
        {daTai && connectors.length === 0 && <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">Chưa có nguồn nào. Thêm KiotViet để nhận đơn và khách hàng tự động.</p>}
        <div className="flex gap-1.5 pt-1">
          <Select value={loaiMoi} onValueChange={(v) => setLoaiMoi(v as SourceType)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{LOAI_HO_TRO.map((k) => <SelectItem key={k} value={k}>{SOURCE_LABELS[k]}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" onClick={tao} disabled={dangTao} title="Thêm nguồn">{dangTao ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}</Button>
        </div>
        <p className="px-1 text-[11px] text-muted-foreground">Google Sheet và FMRP sẽ bổ sung sau; hiện dùng Webhook chung cho các hệ thống khác.</p>
      </div>
      <div>{c ? <FormNguon key={c.id} c={c} onXoa={async () => { if (!confirm(`Xoá nguồn "${c.name}"? Webhook đã đăng ký bên nền tảng ngoài sẽ được gỡ.`)) return; await storeV2.deleteConnector(c.id); setChon(null) }} /> : <EmptyState icon={Plug} title="Chọn hoặc thêm một nguồn dữ liệu" />}</div>
    </div>
  )
}

interface SuKienNhan { id: string; event: string; status: string; runCount: number; error: string | null; receivedAt: string; payload: Record<string, unknown> }

function FormNguon({ c, onXoa }: { c: Connector; onXoa: () => void }) {
  const [ten, setTen] = useState(c.name)
  const [cfg, setCfg] = useState<Record<string, unknown>>({ ...c.config, clientId: '', clientSecret: '' })
  const [hienSecret, setHienSecret] = useState(false)
  const [dang, setDang] = useState<string | null>(null)
  const [suKien, setSuKien] = useState<SuKienNhan[] | null>(null)
  const [khoaKy, setKhoaKy] = useState<string | null>(null)
  const la = (t: SourceType) => c.type === t
  const dongBo = (cfg.dongBo ?? {}) as Record<string, boolean>
  const chiNhanh = (c.config.chiNhanh ?? []) as Array<{ id: number; ten: string }>

  const taiSuKien = async () => {
    try { setSuKien((await api.get<SuKienNhan[]>('/automation-v2/events', { params: { connectorId: c.id, limit: 30 } })).data) } catch { setSuKien([]) }
  }
  useEffect(() => { void taiSuKien() }, [c.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const lam = async <T,>(ten_: string, fn: () => Promise<T>): Promise<T | undefined> => {
    setDang(ten_)
    try { return await fn() } catch (err) { toast.error(apiError(err)); return undefined } finally { setDang(null) }
  }
  const luu = () => lam('luu', async () => {
    const { clientId, clientSecret, ...khac } = cfg
    await storeV2.saveConnector({ ...c, name: ten, config: { ...khac, ...(clientId ? { clientId } : {}), ...(clientSecret ? { clientSecret } : {}) } })
    setCfg((x) => ({ ...x, clientId: '', clientSecret: '' }))
    toast.success('Đã lưu nguồn')
  })
  const kiemTra = () => lam('test', async () => {
    if (cfg.clientId || cfg.clientSecret || ten !== c.name || cfg.retailer !== c.config.retailer) await luu()
    const { data } = await api.post<{ chiNhanh?: Array<{ ten: string }>; thongBao?: string }>(`/automation-v2/connectors/${c.id}/test`)
    await storeV2.taiLai()
    toast.success(data.chiNhanh ? `Kết nối KiotViet thành công — ${data.chiNhanh.length} chi nhánh: ${data.chiNhanh.map((b) => b.ten).join(', ')}` : data.thongBao ?? 'OK')
  })
  const dangKyWebhook = () => lam('webhook', async () => {
    const { data } = await api.post<{ url: string; ketQua: Array<{ type: string; ok: boolean; loi?: string }> }>(`/automation-v2/connectors/${c.id}/webhooks`, {})
    const loi = data.ketQua.filter((k) => !k.ok)
    if (loi.length) toast.error(`Không đăng ký được: ${loi.map((k) => `${k.type} (${k.loi})`).join('; ')}`)
    else toast.success(`Đã đăng ký webhook ${data.ketQua.map((k) => k.type).join(', ')} trên KiotViet`)
  })
  const keo = () => lam('sync', async () => {
    const { data } = await api.post<{ ketQua: Array<{ doiTuong: string; soBanGhi: number; moi: number; trung: number; loi: number; chayLuong: boolean }> }>(`/automation-v2/connectors/${c.id}/sync`)
    await Promise.all([storeV2.taiLai(), taiSuKien()])
    const ten2: Record<string, string> = { customers: 'khách', orders: 'đơn', invoices: 'hoá đơn' }
    toast.success(data.ketQua.map((k) => `${ten2[k.doiTuong] ?? k.doiTuong}: ${k.moi} mới${k.trung ? `, ${k.trung} trùng` : ''}${k.loi ? `, ${k.loi} lỗi` : ''}`).join(' · ') +
      (data.ketQua.some((k) => !k.chayLuong) ? ' — lần đầu chỉ nhập dữ liệu, không chạy luồng' : ''))
  })
  const doiTamDung = () => lam('pause', async () => {
    await storeV2.saveConnector({ ...c, status: c.status === 'paused' ? 'connected' : 'paused' })
  })
  const xemKhoa = () => lam('khoa', async () => { setKhoaKy((await api.get<{ secret: string }>(`/automation-v2/connectors/${c.id}/secret`)).data.secret) })
  const copy = (t: string) => { void navigator.clipboard?.writeText(t); toast.success('Đã sao chép') }

  const O = ({ k, nhan, placeholder, secret, goiY }: { k: string; nhan: string; placeholder?: string; secret?: boolean; goiY?: string }) => (
    <div>
      <Label className="text-xs">{nhan}</Label>
      <div className="relative">
        <Input type={secret && !hienSecret ? 'password' : 'text'} value={String(cfg[k] ?? '')} onChange={(e) => setCfg((x) => ({ ...x, [k]: e.target.value }))} placeholder={placeholder} className="pr-8" autoComplete="off" />
        {secret && <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setHienSecret((v) => !v)}>{hienSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>}
      </div>
      {goiY && <p className="mt-0.5 text-[11px] text-muted-foreground">{goiY}</p>}
    </div>
  )

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input value={ten} onChange={(e) => setTen(e.target.value)} className="h-9 max-w-md font-semibold" />
          <Badge variant="outline">{SOURCE_LABELS[c.type]}</Badge>
          <Badge variant={TRANG_THAI_NGUON[c.status].v}>{TRANG_THAI_NGUON[c.status].nhan}</Badge>
        </div>
        {c.lastError && <p className="mt-2 flex items-start gap-1.5 rounded-md bg-destructive/10 p-2 text-xs text-destructive"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {c.lastError}</p>}
      </CardHeader>
      <CardContent className="space-y-4">
        {la('kiotviet') && (
          <>
            <div className="rounded-md bg-muted/50 p-3 text-xs">
              <p><KeyRound className="mr-1 inline h-3.5 w-3.5" /> Lấy thông tin ở KiotViet → <b>Thiết lập cửa hàng → Thiết lập kết nối API</b>. Client Secret được <b>mã hoá</b> trên máy chủ và không hiển thị lại. Hạn mức KiotViet {KIOTVIET_QUOTA}.</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <O k="retailer" nhan="Tên gian hàng (Retailer)" placeholder="traduocvietnam" goiY="Phần tên trong địa chỉ <tên>.kiotviet.vn" />
              <div className="hidden sm:block" />
              <O k="clientId" nhan={`Client ID${c.config.clientId ? ` (đang dùng ${String(c.config.clientId)})` : ''}`} placeholder={c.coBiMat ? 'Để trống nếu không đổi' : 'Dán Client ID'} />
              <O k="clientSecret" nhan="Client Secret" placeholder={c.coBiMat ? 'Để trống nếu không đổi' : 'Dán Client Secret'} secret />
            </div>
            <div className="space-y-2 rounded-md border p-3 text-xs">
              <p className="font-medium">Đồng bộ</p>
              <label className="flex items-center gap-2"><Switch checked={cfg.tuDongKeo !== false} onCheckedChange={(v) => setCfg((x) => ({ ...x, tuDongKeo: v }))} /> Tự kéo phần thay đổi mỗi 5 phút (dự phòng khi webhook lỡ)</label>
              <div className="flex flex-wrap gap-4 pl-1">
                {[['customers', 'Khách hàng'], ['orders', 'Đơn đặt hàng'], ['invoices', 'Hoá đơn']].map(([k, n]) => (
                  <label key={k} className="flex items-center gap-1.5"><Switch checked={dongBo[k] !== false} onCheckedChange={(v) => setCfg((x) => ({ ...x, dongBo: { ...dongBo, [k]: v } }))} /> {n}</label>
                ))}
              </div>
              <p className="text-muted-foreground">Kéo lần đầu chỉ nhập khách/đơn 24 giờ gần nhất vào CRM và <b>không chạy luồng</b> (không nhắn khách vì đơn cũ). Từ lần sau, mỗi đơn/khách thay đổi sẽ kích hoạt các luồng đang bật.</p>
              {chiNhanh.length > 0 && <p className="text-muted-foreground">Chi nhánh: {chiNhanh.map((b) => b.ten).join(' · ')}</p>}
              <p className="text-muted-foreground">Kéo gần nhất: {fmt(c.lastSyncAt)} · sự kiện cuối: {fmt(c.lastEventAt)}</p>
            </div>
          </>
        )}

        {c.webhookUrl && (
          <div className="space-y-2 rounded-md border p-3 text-xs">
            <p className="flex items-center gap-1.5 font-medium"><Webhook className="h-3.5 w-3.5" /> URL webhook của nguồn này</p>
            <div className="flex items-center gap-2"><code className="flex-1 break-all rounded bg-muted px-2 py-1">{c.webhookUrl}</code><Button size="sm" variant="outline" onClick={() => copy(c.webhookUrl!)}><Copy className="h-3.5 w-3.5" /></Button></div>
            {la('kiotviet') && <p className="text-muted-foreground">Bấm <b>Đăng ký webhook tự động</b> để hệ thống tự khai với KiotViet (đơn, hoá đơn, khách). Hoặc dán URL này vào KiotViet → Quản lý webhook.</p>}
            {la('webhook') && (
              <>
                <p className="text-muted-foreground">Hệ thống khác gửi <code>POST</code> JSON: <code>{'{ "event": "lead.created", "data": { … } }'}</code> (hoặc mảng <code>data</code>). Bắt buộc header <code>X-Signature</code> = HMAC-SHA256 (hex) của nguyên thân request bằng khoá ký bên dưới.</p>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Khoá ký:</span>
                  <code className="rounded bg-muted px-2 py-0.5">{khoaKy ?? '••••••••••'}</code>
                  {khoaKy ? <Button size="sm" variant="ghost" onClick={() => copy(khoaKy)}><Copy className="h-3.5 w-3.5" /></Button> : <Button size="sm" variant="ghost" onClick={xemKhoa} disabled={dang === 'khoa'}>Hiện</Button>}
                </div>
              </>
            )}
          </div>
        )}

        <div>
          <p className="mb-1 text-xs font-medium">Sự kiện nguồn này phát (chọn ở khối Kích hoạt)</p>
          <div className="flex flex-wrap gap-1">{(SOURCE_EVENTS[c.type] ?? []).map((e) => <Badge key={e.key} variant="secondary" title={e.moTa}><code className="mr-1">{e.key}</code>{e.label}</Badge>)}</div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={luu} disabled={!!dang}>{dang === 'luu' && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Lưu</Button>
          {la('kiotviet') && <Button variant="outline" onClick={kiemTra} disabled={!!dang}>{dang === 'test' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Kiểm tra kết nối</Button>}
          {la('kiotviet') && <Button variant="outline" onClick={dangKyWebhook} disabled={!!dang || c.status !== 'connected'} title={c.status !== 'connected' ? 'Kiểm tra kết nối trước' : ''}>{dang === 'webhook' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Webhook className="h-3.5 w-3.5" />} Đăng ký webhook tự động</Button>}
          {la('kiotviet') && <Button variant="outline" onClick={keo} disabled={!!dang || c.status !== 'connected'}>{dang === 'sync' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Kéo dữ liệu ngay</Button>}
          <Button variant="ghost" onClick={doiTamDung} disabled={!!dang}>{c.status === 'paused' ? <><PlayCircle className="h-3.5 w-3.5" /> Bật lại</> : <><PauseCircle className="h-3.5 w-3.5" /> Tạm dừng</>}</Button>
          <Button variant="ghost" className="ml-auto text-destructive" onClick={onXoa}><Trash2 className="h-4 w-4" /> Xoá nguồn</Button>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between"><p className="text-xs font-medium">Sự kiện đã nhận gần đây</p><Button size="sm" variant="ghost" className="h-7 text-xs" onClick={taiSuKien}><RefreshCw className="h-3 w-3" /> Làm mới</Button></div>
          {!suKien ? <p className="text-xs text-muted-foreground">Đang tải…</p> : suKien.length === 0 ? (
            <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">Chưa nhận sự kiện nào. Tạo/sửa một đơn trên KiotViet hoặc bấm “Kéo dữ liệu ngay”.</p>
          ) : (
            <div className="max-h-72 overflow-auto rounded-md border">
              <table className="w-full text-xs">
                <thead><tr className="border-b bg-muted/40 text-left"><th className="p-1.5">Lúc</th><th className="p-1.5">Sự kiện</th><th className="p-1.5">Bản ghi</th><th className="p-1.5">Kết quả</th></tr></thead>
                <tbody>
                  {suKien.map((e) => (
                    <tr key={e.id} className="border-t" title={e.error ?? ''}>
                      <td className="whitespace-nowrap p-1.5 tabular-nums text-muted-foreground">{fmt(e.receivedAt)}</td>
                      <td className="p-1.5"><code>{e.event}</code></td>
                      <td className="max-w-[16rem] truncate p-1.5">{String(e.payload.Code ?? e.payload.Name ?? e.payload.Id ?? '')} {e.payload.StatusValue ? `· ${String(e.payload.StatusValue)}` : ''}</td>
                      <td className="p-1.5">
                        <Badge variant={e.status === 'done' ? 'success' : e.status === 'error' ? 'destructive' : 'secondary'} className="text-[10px]">
                          {e.status === 'done' ? `Chạy ${e.runCount} luồng` : e.status === 'skipped' ? 'Không luồng nào khớp' : e.status === 'error' ? 'Lỗi' : 'Đang xử lý'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
